// Kollisions- und Raumprüfung — reine Geometrie, engine-frei (kein Three.js).
// Teile sind orientierte Quader (OBB): lokale Achsen X = Länge, Y = Stärke,
// Z = Breite, Rotation Euler XYZ in Grad — exakt wie der Renderer
// (mesh.rotation.set(rot_x*DEG, rot_y*DEG, rot_z*DEG)).
// Bewusste Vereinfachung: geprüft wird der volle Quader — Ausschnitte,
// Nuten und sonstige Bearbeitungen (CSG) bleiben außen vor.

import { partAabb } from "./snapping"
import { isHidden } from "./visibility"

const DEG = Math.PI / 180

// Durchdringungen bis 0,5 mm gelten als Berührung, nicht als Kollision —
// aneinandergestoßene Teile sind der Normalfall
export const CONTACT_TOLERANCE_MM = 0.5

// Rotationsmatrix Euler XYZ in Grad; Spalten = lokale Achsen in
// Weltkoordinaten (gleiche Konvention wie snapping.js)
function rotationMatrix(rxDeg, ryDeg, rzDeg) {
  const [cx, sx] = [Math.cos(rxDeg * DEG), Math.sin(rxDeg * DEG)]
  const [cy, sy] = [Math.cos(ryDeg * DEG), Math.sin(ryDeg * DEG)]
  const [cz, sz] = [Math.cos(rzDeg * DEG), Math.sin(rzDeg * DEG)]
  const rx = [[1, 0, 0], [0, cx, -sx], [0, sx, cx]]
  const ry = [[cy, 0, sy], [0, 1, 0], [-sy, 0, cy]]
  const rz = [[cz, -sz, 0], [sz, cz, 0], [0, 0, 1]]
  const mul = (a, b) => a.map((row, i) => row.map((_, j) => a[i][0] * b[0][j] + a[i][1] * b[1][j] + a[i][2] * b[2][j]))
  return mul(mul(rx, ry), rz)
}

function obbOf(part) {
  return {
    center: [part.pos_x, part.pos_y, part.pos_z],
    half: [part.length_mm / 2, part.thickness_mm / 2, part.width_mm / 2],
    rotation: rotationMatrix(part.rot_x, part.rot_y, part.rot_z)
  }
}

// Exakter Überschneidungstest zweier Teile als orientierte Boxen:
// Separating-Axis-Test mit allen 15 Achsen (3+3 Flächennormalen,
// 9 Kantenkreuzprodukte). true nur, wenn die Durchdringung auf JEDER
// möglichen Trennachse größer als die Toleranz ist.
export function obbsCollide(partA, partB, tolerance = CONTACT_TOLERANCE_MM) {
  const a = obbOf(partA)
  const b = obbOf(partB)

  // Rotation von B im A-System: R[i][j] = Achse_i(A) · Achse_j(B)
  const R = [[], [], []]
  const absR = [[], [], []]
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) {
      let dot = 0
      for (let k = 0; k < 3; k++) dot += a.rotation[k][i] * b.rotation[k][j]
      R[i][j] = dot
      absR[i][j] = Math.abs(dot)
    }
  }

  // Zentrenversatz, ins A-System gedreht
  const dWorld = [0, 1, 2].map((i) => b.center[i] - a.center[i])
  const t = [0, 1, 2].map((i) =>
    a.rotation[0][i] * dWorld[0] + a.rotation[1][i] * dWorld[1] + a.rotation[2][i] * dWorld[2])

  // Flächennormalen von A
  for (let i = 0; i < 3; i++) {
    const radius = a.half[i] + b.half[0] * absR[i][0] + b.half[1] * absR[i][1] + b.half[2] * absR[i][2]
    if (Math.abs(t[i]) >= radius - tolerance) return false
  }

  // Flächennormalen von B
  for (let j = 0; j < 3; j++) {
    const projection = Math.abs(t[0] * R[0][j] + t[1] * R[1][j] + t[2] * R[2][j])
    const radius = b.half[j] + a.half[0] * absR[0][j] + a.half[1] * absR[1][j] + a.half[2] * absR[2][j]
    if (projection >= radius - tolerance) return false
  }

  // Kantenkreuzprodukte Achse_i(A) × Achse_j(B); (nahezu) parallele Kanten
  // ergeben eine Null-Achse und werden übersprungen. Die Toleranz gilt in mm
  // entlang der Achse — deshalb mit der Achsenlänge skalieren.
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) {
      const lengthSq = 1 - R[i][j] * R[i][j]
      if (lengthSq < 1e-8) continue
      const [i1, i2] = [(i + 1) % 3, (i + 2) % 3]
      const [j1, j2] = [(j + 1) % 3, (j + 2) % 3]
      const projection = Math.abs(t[i2] * R[i1][j] - t[i1] * R[i2][j])
      const radius =
        a.half[i1] * absR[i2][j] + a.half[i2] * absR[i1][j] +
        b.half[j1] * absR[i][j2] + b.half[j2] * absR[i][j1]
      if (projection >= radius - tolerance * Math.sqrt(lengthSq)) return false
    }
  }

  return true
}

// AABB-Vorfilter: nur Paare, deren Welt-AABBs sich um mehr als die Toleranz
// schneiden, kommen in den OBB-Test — konservativ, kann also nie ein
// tatsächlich kollidierendes Paar aussortieren.
function aabbsOverlap(a, b, tolerance) {
  return [0, 1, 2].every((axis) =>
    a.min[axis] < b.max[axis] - tolerance && b.min[axis] < a.max[axis] - tolerance)
}

// Alle kollidierenden Paare als [idA, idB]; versteckte Teile zählen nicht.
// O(n²) mit AABB-Vorfilter reicht für Werkstatt-Projekte locker.
export function collidingPairs(parts, tolerance = CONTACT_TOLERANCE_MM) {
  const visible = parts.filter((part) => !isHidden(part))
  const aabbs = visible.map((part) => partAabb(part))
  const pairs = []
  for (let i = 0; i < visible.length; i++) {
    for (let j = i + 1; j < visible.length; j++) {
      if (!aabbsOverlap(aabbs[i], aabbs[j], tolerance)) continue
      if (obbsCollide(visible[i], visible[j], tolerance)) {
        pairs.push([visible[i].client_id, visible[j].client_id])
      }
    }
  }
  return pairs
}

// Teile, deren Welt-AABB die Raumgrenzen um mehr als die Toleranz verlässt.
// Raum wie im Renderer: X ±width/2, Boden y = 0 bis height, Z ±depth/2.
// Nur bei aktivem Raum; versteckte Teile werden übersprungen.
export function outsideRoomIds(parts, room, tolerance = CONTACT_TOLERANCE_MM) {
  if (room?.enabled !== true) return []
  const min = [-room.width_mm / 2, 0, -room.depth_mm / 2]
  const max = [room.width_mm / 2, room.height_mm, room.depth_mm / 2]
  return parts
    .filter((part) => !isHidden(part))
    .filter((part) => {
      const aabb = partAabb(part)
      return [0, 1, 2].some((axis) =>
        aabb.min[axis] < min[axis] - tolerance || aabb.max[axis] > max[axis] + tolerance)
    })
    .map((part) => part.client_id)
}
